// app/javascript/projects/components/AnalysisExportModal.jsx

import React, { useContext } from 'react';
import { Button, Modal } from 'ui';
import { useExportMatrixActions } from '../../../hooks/exports/useExportData';
import { useSnippetExport } from '../../../hooks/exports/useSnippetExport';
import ModalComponent from '../modal/ModalComponent';
import FilterStateContext from '../../../contexts/FilterStateContext';

const MARGIN = 20;

const AnalysisExportModal = ({ show, onHide, projectId }) => {
  // Try to get filters from context if available, otherwise use empty filters
  const filterStateContext = useContext(FilterStateContext);
  const filters = filterStateContext?.filters || {};

  // Only pass projectId when modal is shown to prevent premature data fetching
  const effectiveProjectId = show ? projectId : null;

  // Matrix exports
  const {
    isReady,
    isMetadataLoading,
    exportCodesVCodes,
    exportCodesVTranscripts,
    exportCodesVDescriptor,
    isExportingCodesVCodes,
    isExportingCodesVTranscripts,
    isExportingCodesVDescriptor,
    codesVCodesError,
    codesVTranscriptsError,
    codesVDescriptorError
  } = useExportMatrixActions(effectiveProjectId);

  // Snippet export (separate hook because it needs filters)
  const {
    exportSnippets: exportSnippetsAction,
    isExporting: isExportingSnippets,
    isReady: snippetsReady
  } = useSnippetExport(effectiveProjectId, filters);

  const exportSnippets = () => {
    if (snippetsReady) {
      exportSnippetsAction();
    }
  };

  const handleCodesVCodesExport = () => {
    if (isReady) {
      exportCodesVCodes(); // Pass codeIds if needed for specific codes
    }
  };

  const handleCodesVTranscriptsExport = () => {
    if (isReady) {
      exportCodesVTranscripts();
    }
  };

  const handleCodesVDescriptorExport = () => {
    if (isReady) {
      exportCodesVDescriptor();
    }
  };

  const isAnyExportLoading = isExportingCodesVCodes || isExportingCodesVTranscripts || isExportingCodesVDescriptor || isExportingSnippets;

  return (
    <div>
      <Modal show={show} onHide={onHide}>
        <Modal.Body>
          <ModalComponent indent header margin={MARGIN}>
            <h3>Exports</h3>
          </ModalComponent>

          <ModalComponent indent margin={MARGIN}>
            <Button disabled={!snippetsReady || isAnyExportLoading} variant='link' style={{padding: 0}} onClick={exportSnippets}>
              <strong>Export Snippets as CSV</strong>
              {isExportingSnippets && ' (Loading...)'}
            </Button>
            <p>
              See a spreadsheet of snippets with their transcripts, codes, descriptors, and memos.
            </p>
            <p>
              To export a segment of data, use filters on Snippet page.
            </p>
          </ModalComponent>

          <ModalComponent indent margin={MARGIN}>
            <Button disabled={!isReady || isExportingCodesVCodes || isAnyExportLoading} variant='link' style={{padding: 0}} onClick={handleCodesVCodesExport}>
              <strong>Export Code x Code Matrix CSV</strong>
              {isExportingCodesVCodes && ' (Loading...)'}
            </Button>
            <p>
              See how codes co-occur and overlap
            </p>
          </ModalComponent>

          <ModalComponent indent margin={MARGIN}>
            <Button disabled={!isReady || isExportingCodesVTranscripts || isAnyExportLoading} variant='link' style={{padding: 0}} onClick={handleCodesVTranscriptsExport}>
              <strong>Export Code x Transcript Matrix CSV</strong>
              {isExportingCodesVTranscripts && ' (Loading...)'}
            </Button>
            <p>
              See frequency of codes in each transcript
            </p>
          </ModalComponent>

          <ModalComponent footer indent margin={MARGIN}>
            <Button disabled={!isReady || isExportingCodesVDescriptor || isAnyExportLoading} variant='link' style={{padding: 0}} onClick={handleCodesVDescriptorExport}>
              <strong>Export Code x Descriptor Matrix CSV</strong>
              {isExportingCodesVDescriptor && ' (Loading...)'}
            </Button>
            <p>
              See how participant information correlates with codes
            </p>
          </ModalComponent>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AnalysisExportModal;